import React, { useState, useRef, useEffect } from "react";
import Lottie from "lottie-react";
import { emitter } from '../../../events';
import iAThinking from "../../../animations/loading-ia.json";

import { Modal, Button, Row, Col, Table, Select } from "antd";
import { doctorIaUrl } from "../../../config";
import { useTranslation } from "react-i18next";
import { getBranchOffice } from "../../../services/general";
import { getCookie } from "../../../services/cookies";

const DEFAULT_MODEL = "default";

const IAResults = ({ getAsset, document, examInfo, type, file }) => {
  const [results, setResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [additionalModels, setAdditionalModels] = useState({});
  const [selectedModel, setSelectedModel] = useState(DEFAULT_MODEL);
  const [modelConfirmed, setModelConfirmed] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);

  const abortControllerRef = useRef(null);
  const { t } = useTranslation();

  const branchOfficeId = examInfo?.patient?.branch_office_id || getCookie('branch_office_id');
  const hasAdditionalModels = Object.keys(additionalModels).length > 0;

  useEffect(() => {
    const fetchAiConfig = async () => {
      if (!branchOfficeId) return;
      setConfigLoading(true);
      try {
        const branchOffice = await getBranchOffice(branchOfficeId);
        setAdditionalModels(branchOffice?.extra_data?.ai_config?.[type] || {});
      } catch (error) {
        console.error('Error al obtener configuración de IA de la sucursal:', error);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchAiConfig();
  }, [branchOfficeId, type]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchResults = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const procedures = examInfo.exams
      .flatMap((item) => item.inputs.procedures)
      .map((procedure) => ({
        id: procedure.id,
        name: procedure.name,
        code: procedure.code,
      }));
    
      const units = examInfo.exams
        .flatMap((item) => item.inputs.procedures)
        .map((procedure) => ({
          id: procedure.id,
          unit: procedure.unit,
        }));

    setLoading(true);

  try {
    let url;
    const isLab = type === 'lab';
    if (isLab && document.id) {
      url = await getAsset({
        field: "document",
        id: document.id,
        type: document.type,
      });
    }

    const formdata = new FormData();

    if (isLab && url?.document_url) {
      formdata.append('file_url', url.document_url);
    } else if (file && file.uid === '-1') {
      const fileAsset = await getAsset({
        field: file.field,
        id: file.id,
        type: file.document_type,
      });
      formdata.append('file_url', fileAsset.document_url);
    } else {
      formdata.append('file', file);
    }

    formdata.append("procedures", JSON.stringify(procedures));
    formdata.append("units", JSON.stringify(units));
    formdata.append("branch_office_id", branchOfficeId);

    const admissionPatientId = examInfo.patient?.admission_patient_id;
    formdata.append("admission_patient_id", admissionPatientId);

    if (selectedModel !== DEFAULT_MODEL) {
      formdata.append("ai_model", selectedModel);
    }

    let iaUrl;
    if (isLab) {
      iaUrl = `${doctorIaUrl}/transcription_v2?type=${type}`;
    } else if (type === 'audio') {
      iaUrl = `${doctorIaUrl}/analyze_audiometry`;
    } else {
      iaUrl = `${doctorIaUrl}/transcription_image?type=${type}`;
    }

    const response = await fetch(iaUrl, {
      method: "POST",
      body: formdata,
      redirect: "follow",
      signal: abortControllerRef.current.signal,
    });

    const data = await response.json();
    setResults(data.results);
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error al obtener resultados de IA:', error);
    }
  } finally {
    setLoading(false);
  }
};

  const handleShowModal = () => {
    setShowModal(true);
    if (hasAdditionalModels) {
      setSelectedModel(Object.keys(additionalModels)[0]);
    } else {
      fetchResults();
    }
  };

  const handleConfirmModel = () => {
    setModelConfirmed(true);
    fetchResults();
  };

  const resetModalState = () => {
    setShowModal(false);
    setLoading(false);
    setResults([]);
    setModelConfirmed(false);
    setSelectedModel(Object.keys(additionalModels)[0] || DEFAULT_MODEL);
  };

  const handleCloseModal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    resetModalState();
  };

  const sendData = () => {
    emitter.emit(type === 'audio' ? 'audiometryIAData' : 'labData', { data: results });
    resetModalState();
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Resultado",
      dataIndex: "result",
      key: "result",
      render: (text) => <div>{text}</div>,
    },
    {
      title: "Unidad",
      dataIndex: "unit",
      key: "unit",
      render: (text) => <div>{text}</div>,
    },
  ];

  const awaitingModelSelection = hasAdditionalModels && !modelConfirmed;

  const modelOptions = [
    ...Object.entries(additionalModels).map(([key, modelConfig]) => ({
      value: key,
      label: modelConfig?.label ?? key,
    })),
    { value: DEFAULT_MODEL, label: "General" },
  ];

  return (
    <Row>
      <Col>
        <Modal
          className="ai-results-modal"
          title="Resultados de la IA"
          open={showModal}
          onOk={awaitingModelSelection ? handleConfirmModel : sendData}
          onCancel={handleCloseModal}
          okButtonProps={{
            className: "ai-results-ok-button",
            disabled: awaitingModelSelection ? false : (loading || results?.length === 0)
          }}
          cancelText="Cancelar"
          okText={awaitingModelSelection ? "Continuar" : "Aceptar"}
        >
          {awaitingModelSelection ? (
            <Select
              className="cmt-select ai-results-step"
              style={{ width: "100%" }}
              value={selectedModel}
              onChange={setSelectedModel}
              options={modelOptions}
            />
          ) : loading ? (
            <div className="ai-results-loading ai-results-step">
              <Lottie
                animationData={iAThinking}
                loop
                style={{ width: 100, height: 100 }}
              />
            </div>
          ) : results?.length > 0 ? (
            <Table
              className="ai-results-step"
              dataSource={results}
              columns={columns}
              rowKey={(record, index) => index}
              pagination={false}
            />
            ) : (
            <div className="text-center ai-results-step">
              {t('IAResults.emptyState')}
            </div>
            )}
        </Modal>

        <Button
          className="m-5"
          type="dashed"
          onClick={handleShowModal}
          shape="circle"
          loading={loading}
          disabled={loading || configLoading}
          icon={
            <img
              src="https://cmtsalud-resources.s3.us-west-2.amazonaws.com/ai.svg"
              alt="AI Icon"
              style={{
                width: "1.2em",
                height: "1.2em",
                verticalAlign: "middle",
              }}
            />
          }
        />
      </Col>
    </Row>
  );
};

export default IAResults;
